import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import PartnerSidebar from './PartnerSidebar';
import LoginPopupModal from '../common/LoginPopupModal';
import ConsentModal from '../common/ConsentModal';
import BrandMark from '../BrandMark';
import { Toaster } from '../ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerLayout = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [partnerStatus, setPartnerStatus] = useState(localStorage.getItem('partnerStatus') || 'Active');
  const navigate = useNavigate();
  const location = useLocation();
  const partnerName = localStorage.getItem('partnerName') || 'Partner';

  // Fetch partner status
  useEffect(() => {
    const fetchStatus = async () => {
      const token = localStorage.getItem('partnerToken');
      if (!token) return;
      try {
        const response = await axios.get(`${API}/partner/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const status = response.data.status || 'Active';
        setPartnerStatus(status);
        localStorage.setItem('partnerStatus', status);
      } catch (error) {
        console.error('Failed to fetch partner status:', error);
      }
    };
    fetchStatus();
  }, []);

  // Check consent status
  useEffect(() => {
    const checkConsent = async () => {
      const token = localStorage.getItem('partnerToken');
      if (!token) return;

      try {
        const response = await axios.get(`${API}/partner/consent-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const { consent_given, consent_required } = response.data;

        if (consent_required && !consent_given) {
          setShowConsentModal(true);
        } else {
          setConsentChecked(true);
        }
      } catch (error) {
        console.error('Failed to check consent status:', error);
        setConsentChecked(true); // Allow access if check fails
      }
    };

    checkConsent();
  }, []);

  const handleConsentGiven = () => {
    setShowConsentModal(false);
    setConsentChecked(true);
  };

  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('partnerToken');
    if (!token) {
      navigate('/');
    }
  }, [navigate, location]);

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Show login popup on first load (after successful login)
  useEffect(() => {
    const token = localStorage.getItem('partnerToken');
    const justLoggedIn = sessionStorage.getItem('partner_just_logged_in');

    if (token && justLoggedIn) {
      sessionStorage.removeItem('partner_just_logged_in');
      setShowLoginPopup(true);
    }
  }, []);

  const handleLogout = async () => {
    const token = localStorage.getItem('partnerToken');
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) { /* ignore */ }
    }
    localStorage.removeItem('partnerToken');
    localStorage.removeItem('partnerAccessToken');
    localStorage.removeItem('partnerRefreshToken');
    localStorage.removeItem('partnerEmail');
    localStorage.removeItem('partnerId');
    localStorage.removeItem('partnerName');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar (desktop + mobile drawer) */}
      <PartnerSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        onLogout={handleLogout}
        partnerStatus={partnerStatus}
        isMobileOpen={isMobileOpen}
        onMobileClose={() => setIsMobileOpen(false)}
      />

      {/* Mobile backdrop */}
      {isMobileOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setIsMobileOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 z-[55]"
          data-testid="partner-mobile-backdrop"
        />
      )}

      {/* Main Content */}
      <div
        className={`min-h-screen transition-all duration-300 ${
          isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'
        }`}
      >
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-50 flex items-center gap-3 h-14 px-4 bg-slate-900/95 backdrop-blur border-b border-slate-800">
          <button
            onClick={() => setIsMobileOpen(true)}
            className="p-2 -ml-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 flex-shrink-0"
            aria-label="Open menu"
            data-testid="partner-mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <a href="/partner/dashboard" className="flex items-center gap-2 min-w-0 flex-1">
            <BrandMark size="sm" showText={true} />
          </a>
          <span className="text-xs text-slate-400 truncate max-w-[40vw]" title={partnerName}>
            {partnerName}
          </span>
        </header>

        <main className="p-4 md:p-8 max-w-full overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Login Popup Modal */}
      {showLoginPopup && (
        <LoginPopupModal
          type="partner"
          onClose={() => setShowLoginPopup(false)}
        />
      )}

      {/* Consent Modal - Blocks access until consent is given */}
      {showConsentModal && (
        <ConsentModal
          userType="partner"
          token={localStorage.getItem('partnerToken')}
          onConsentGiven={handleConsentGiven}
        />
      )}

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default PartnerLayout;
